import React, { useState } from 'react';
import { Box, Grid, Typography, RadioGroup, Paper, Switch, Divider, Button, Alert } from '@mui/material';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const PLANS = {
  basic: {
    id: 'basic',
    name: 'Guidance',
    price: 9,
    features: 'Scientific Guidance on your portfolio and detailed recommendations',
    stripePriceId: 'price_1QrIu4LXIfZ6kU0NMlRwSPkn'
  },
  premium: {
    id: 'premium',
    name: 'Guidance + Monitoring',
    price: 15,
    features: 'Everything in Guidance + you get e-mail notifications when your portfolio needs rebalancing',
    stripePriceId: 'price_1R9STaLXIfZ6kU0NHeweaQ8b'
  }
};

const PaymentForm = ({ onPaymentSuccess, onPaymentError, formData, setFormData }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [selectedPlan, setSelectedPlan] = useState(PLANS.basic.id);
  const [isYearly, setIsYearly] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);

  const handlePlanChange = (planId) => {
    setSelectedPlan(planId);
    setFormData(prev => ({
      ...prev,
      planId,
      planPrice: isYearly ? PLANS[planId].price * 10 : PLANS[planId].price,
      billingInterval: isYearly ? 'yearly' : 'monthly',
      stripePriceId: PLANS[planId].stripePriceId
    }));
  };

  const handlePaymentSubmit = async () => {
    if (!stripe || !elements) return false;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      onPaymentError('Card Element not found');
      return false;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email
      }
    });

    if (error) {
      onPaymentError(error.message);
      return false;
    }

    setFormData(prev => ({
      ...prev,
      stripePaymentMethodId: paymentMethod.id
    }));

    setPaymentCompleted(true);
    onPaymentSuccess();
    return true;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography>Monthly</Typography>
            <Switch
              checked={isYearly}
              onChange={(e) => {
                setIsYearly(e.target.checked);
                // Update the plan price based on billing interval change
                setFormData(prev => ({
                  ...prev,
                  planPrice: e.target.checked ? PLANS[selectedPlan].price * 10 : PLANS[selectedPlan].price,
                  billingInterval: e.target.checked ? 'yearly' : 'monthly'
                }));
              }}
            />
            <Typography>Yearly (Save 17%)</Typography>
          </Box>
          <RadioGroup
            value={selectedPlan}
            onChange={(e) => handlePlanChange(e.target.value)}
          >
            <Grid container spacing={2}>
              {Object.values(PLANS).map((plan) => (
                <Grid item xs={12} sm={6} key={plan.id}>
                  <Paper
                    elevation={selectedPlan === plan.id ? 3 : 1}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: (t) =>
                        selectedPlan === plan.id
                          ? `2px solid ${t.palette.primary.main}`
                          : '2px solid transparent'
                    }}
                    onClick={() => handlePlanChange(plan.id)}
                  >
                    <Typography variant="h6">{plan.name}</Typography>
                    <Typography variant="h4" color="primary">
                      ${isYearly ? (plan.price * 11).toFixed(2) : plan.price.toFixed(2)}
                      <Typography component="span" variant="body2" color="text.secondary">
                        /{isYearly ? 'year' : 'month'}
                      </Typography>
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      {plan.features}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ my: 3 }} />
          <Paper variant="outlined" sx={{ p: 3 }}>
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': { color: '#aab7c4' }
                  },
                  invalid: { color: '#9e2146' }
                }
              }}
            />
          </Paper>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <img
              src="https://images.stripeassets.com/fzn2n1nzq965/4M6d6BSWzlgsrJx8rdZb0I/733f37ef69b5ca1d3d33e127184f4ce4/Powered_by_Stripe.svg?q=80&w=1082"
              alt="Stripe"
              style={{ height: 20, marginRight: 8 }}
            />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {paymentCompleted && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Payment information saved successfully.
            </Alert>
          )}
          <Button
            variant="contained"
            onClick={handlePaymentSubmit}
            disabled={paymentCompleted}
          >
            {paymentCompleted ? 'Payment Info Saved' : 'Submit Payment'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaymentForm;
